import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.jsx';
import './index.css';
// Initialize i18next for multi-language support
import './i18n/config';

// Initialize Sentry for remote error tracking and console log capture
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' or 'production'

    // Capture 100% of transactions for debugging (reduce in production later)
    tracesSampleRate: 1.0,

    // debug.js adds its own structured breadcrumbs (category: "debug"),
    // so disable console.log/warn breadcrumbs to avoid duplicates.
    // Keep console.error enabled so ErrorBoundary and uncaught errors are captured.
    integrations: [
      Sentry.breadcrumbsIntegration({ console: true }),
      Sentry.browserTracingIntegration(),
    ],

    // Filter out duplicate console.log/warn breadcrumbs (debug.js already
    // adds these as category:"debug"). Only keep console.error breadcrumbs.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null; // drop console.log/warn duplicates
      }
      return breadcrumb;
    },

    // Drop noisy network errors that aren't actionable.
    // iOS "Load failed" / Chrome "Failed to fetch" are transient connectivity
    // issues. Minified PostgREST class names (single short token like "Nd")
    // are the same errors without a readable message.
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || '';
      // iOS network error or Chrome network error
      if (/Load failed|Failed to fetch|NetworkError/i.test(message)) {
        return null;
      }
      // Minified PostgREST error class name (1-3 chars, no spaces)
      // These are network errors where the message is just the mangled class name
      if (/^[A-Za-z]{1,3}$/.test(message)) {
        return null;
      }
      // Third-party browser errors (DuckDuckGo, Brave, etc.) that inject
      // scripts into the page. Not from our code, not actionable.
      if (/feature named `.+` was not found/.test(message)) {
        return null;
      }
      return event;
    },

    // 2000 breadcrumbs covers full booking sessions (476 seen in simple test,
    // sessions with modal interactions can exceed 500).
    maxBreadcrumbs: 2000,

    // Send user agent, useful for Android/Chrome debugging
    sendDefaultPii: false,
  });
}

createRoot(document.getElementById('root')).render(
<StrictMode>
    <App />
</StrictMode>
);